// web/src/content/legal.ts
//
// Single source of truth for the Privacy Policy + Terms of Service: version
// strings AND body content. Both PrivacyPolicy.tsx / Terms.tsx render from
// here, and the consent flow (ConsentGate, the consent actions on api/account.ts) record exactly
// the version string exported below — so bumping a version here is what
// forces returning users to re-accept.
//
// IMPORTANT: bump the relevant *_VERSION constant any time the corresponding
// document body changes. A clickwrap acceptance only binds to the exact
// version the user saw — an unbumped version on changed copy is a compliance
// gap, not just a display nit.
//
// TODO: final copy supplied separately (external legal review). Everything
// below is placeholder structure/wording so the versioning, routing, and
// consent plumbing can be built and tested ahead of the real copy landing.

export const PRIVACY_POLICY_VERSION = '2026-07-28.1';
export const TERMS_VERSION = '2026-07-28.1';

export interface LegalSection {
  heading: string;
  paragraphs: string[];
}

export interface LegalDocument {
  title: string;
  version: string;
  effectiveDate: string;
  sections: LegalSection[];
}

export const PRIVACY_POLICY: LegalDocument = {
  title: 'Privacy Policy',
  version: PRIVACY_POLICY_VERSION,
  effectiveDate: '2026-07-28',
  sections: [
    {
      heading: 'TODO: final copy supplied separately',
      paragraphs: [
        'This is placeholder structure only — the real privacy policy text will ' +
          'be supplied by legal review and dropped into this file. The sections ' +
          'below outline the headings a Privacy Act 1988 (Cth) / Australian ' +
          "Privacy Principles-compliant policy is expected to cover, so the " +
          'page, routing, and consent-versioning plumbing can be built and ' +
          'tested ahead of the final wording.',
      ],
    },
    {
      heading: 'What we collect',
      paragraphs: [
        'TODO: final copy supplied separately. Placeholder — describes account ' +
          'identifiers (Google sign-in, wallet address), shop visit/stamp ' +
          'activity, and any optional profile fields (display name, avatar, ' +
          'birthday month/day).',
      ],
    },
    {
      heading: 'How we use it',
      paragraphs: [
        'TODO: final copy supplied separately. Placeholder — service delivery ' +
          '(loyalty stamps, rewards, receipts), fraud prevention, and — only ' +
          'where the customer has separately opted in — marketing messages.',
      ],
    },
    {
      heading: 'Marketing and your choices',
      paragraphs: [
        'TODO: final copy supplied separately. Placeholder — marketing email ' +
          'and push notifications are opt-in only and never bundled with ' +
          'account creation. Every marketing message includes a functional ' +
          'unsubscribe, consistent with the Spam Act 2003 (Cth). Preferences ' +
          'can be changed at any time in account settings.',
      ],
    },
    {
      heading: 'Sharing and third parties',
      paragraphs: [
        'TODO: final copy supplied separately. Placeholder — covers payment ' +
          'processing (Square) and any other processors used to run the service.',
      ],
    },
    {
      heading: 'Your rights',
      paragraphs: [
        'TODO: final copy supplied separately. Placeholder — access, correction, ' +
          'and deletion requests, and how to make one.',
      ],
    },
    {
      heading: 'Contact',
      paragraphs: [
        'TODO: final copy supplied separately. Placeholder — contact details for ' +
          'privacy questions or complaints.',
      ],
    },
  ],
};

export const TERMS: LegalDocument = {
  title: 'Terms of Service',
  version: TERMS_VERSION,
  effectiveDate: '2026-07-28',
  sections: [
    {
      heading: 'TODO: final copy supplied separately',
      paragraphs: [
        'This is placeholder structure only — the real terms of service text ' +
          'will be supplied by legal review and dropped into this file. The ' +
          'sections below outline the headings expected in the final ' +
          'agreement, so the page, routing, and consent-versioning plumbing ' +
          'can be built and tested ahead of the final wording.',
      ],
    },
    {
      heading: 'Using the service',
      paragraphs: [
        'TODO: final copy supplied separately. Placeholder — eligibility, ' +
          'account responsibilities, and acceptable use.',
      ],
    },
    {
      heading: 'Rewards and stamps',
      paragraphs: [
        'TODO: final copy supplied separately. Placeholder — how stamps and ' +
          'rewards are earned, redeemed, and expired, and that participating ' +
          'shops set their own reward rules.',
      ],
    },
    {
      heading: 'Payments',
      paragraphs: [
        'TODO: final copy supplied separately. Placeholder — payment processing ' +
          'is handled by Square; billing terms for shop subscriptions live here.',
      ],
    },
    {
      heading: 'Limitation of liability',
      paragraphs: [
        'TODO: final copy supplied separately.',
      ],
    },
    {
      heading: 'Changes to these terms',
      paragraphs: [
        'TODO: final copy supplied separately. Placeholder — material changes ' +
          'bump the document version above and require re-acceptance on next ' +
          'sign-in.',
      ],
    },
    {
      heading: 'Contact',
      paragraphs: [
        'TODO: final copy supplied separately.',
      ],
    },
  ],
};

// ── Merchant terms ─────────────────────────────────────────────────────────
//
// Accepted at shop registration, separately from the consumer Terms. Shop
// owners receive identifiable customer data through the owner dashboard, so
// they need obligations the consumer Terms do not impose.
//
// The posture is: minimise what leaves, then contract for the residue. The
// technical half is already enforced in code — owner analytics select
// display_name only and never email (api/gamification.ts:279, :407), comp-send
// keeps the address server-side and returns only 'customer_no_email' to the
// owner (:499), an anti-oracle guard (:415) refuses address→name resolution
// for non-customers, and there is no export anywhere in the owner UI. This
// document covers what code cannot reach.
//
// TODO: reward-liability and "founders for life" clauses want qualified review
// before paid launch. Versioning means the text can be replaced without a code
// change — bump MERCHANT_TERMS_VERSION and every owner re-accepts.
export const MERCHANT_TERMS_VERSION = '2026-07-29.1';

export const MERCHANT_TERMS: LegalDocument = {
  title: 'Merchant Terms',
  version: MERCHANT_TERMS_VERSION,
  effectiveDate: '29 July 2026',
  sections: [
    {
      heading: 'What we promise you',
      paragraphs: [
        'We never give you your customers\' contact details — no email addresses, no phone numbers — and we never give them to anyone else either. You see a display name and that customer\'s history at your shop, which is what you need to run a loyalty program.',
        'You can still reach your customers through Bit Brew: send a comp, run a happy hour, or publish a special. We deliver it on your behalf without the contact details ever leaving our systems.',
      ],
    },
    {
      heading: 'How you may use customer information',
      paragraphs: [
        'You may use what you see in your dashboard to run your loyalty program and serve your customers in your shop.',
        'You may not re-identify Bit Brew customers, or match display names against your own point-of-sale, mailing list or marketing records, to build a contactable list.',
        'You may not scrape, bulk-copy or automate collection from the dashboard to reconstruct a customer list outside Bit Brew.',
        'You may not disclose customer information from Bit Brew to any third party.',
      ],
    },
    {
      heading: 'Rewards are your promise',
      paragraphs: [
        'The free coffee, discount or other reward a customer earns at your shop is yours to honour. Bit Brew records stamps and reward eligibility; we do not supply the reward itself.',
        'If you stop trading or leave Bit Brew, you remain responsible for rewards your customers have already earned and not yet claimed.',
      ],
    },
    {
      heading: 'Billing',
      paragraphs: [
        'Pricing, billing period and any founder pricing are as shown to you when you subscribe. Founder pricing, where offered, is held for as long as your shop keeps an unbroken subscription; it does not survive cancellation and re-subscription.',
        'If a payment fails or a subscription lapses, your shop may lose access to paid features. Your customers keep every stamp they have earned — their records are theirs, not ours and not yours.',
      ],
    },
    {
      heading: 'Square and other connected services',
      paragraphs: [
        'If you connect Square or another point-of-sale service, we receive transaction data for the purpose of matching payments to stamps. We use it for that purpose and to give you your own shop analytics.',
        'You can disconnect a connected service at any time from your shop settings.',
      ],
    },
    {
      heading: 'Suspension',
      paragraphs: [
        'We may suspend or remove a shop that issues stamps for purchases that did not happen, harvests or misuses customer information, publishes unlawful or offensive content in any shop-authored field, or refuses to honour rewards its customers have earned.',
        'Where it is reasonable to do so, we will contact you before suspending your shop.',
      ],
    },
    {
      heading: 'Termination',
      paragraphs: [
        'You may close your shop at any time. Your access to the owner dashboard and to customer information ends when your shop closes.',
        'Your customers\' relationship is with Bit Brew and with themselves — closing your shop does not transfer any customer information to you, and does not delete your customers\' own records.',
      ],
    },
  ],
};
